import { appIntl } from '@nxsys/helpers/Common';
import {
  GetAgencyBranchesListParams,
  GetAgencyContactsListParams,
  GetAgencyDocumentListParams,
  GetAgencyListParams,
  GetAgencyTimesheetListParams,
} from '@nxsys/store/slices/agency/types';
import { PaginationType } from '@nxsys/types/models/Common';
import { DocumentType } from '@nxsys/types/models/document';
import { AgencyContactType } from '@nxsys/types/models/permissions';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { Assignment, GetAssignmentListParams } from '../../assignment/types';
import { agencyActs } from '../actions';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { GetEmployeeListParams } from '../../employee/types';

export type AgencyState = {
  agencyList: {
    loading: boolean;
    list: IAgency[];
    pagination: PaginationType;
    queryParams: GetAgencyListParams | null;
  };
  agencyDocumentList: {
    loading: boolean;
    list: DocumentType[];
    pagination: PaginationType;
    queryParams: GetAgencyDocumentListParams | null;
  };
  agencyTimesheetList: {
    loading: boolean;
    list: TimeSheetDataItem[];
    pagination: PaginationType;
    queryParams: GetAgencyTimesheetListParams | null;
  };
  agencyBranchList: {
    loading: boolean;
    list: IBranch[];
    pagination: PaginationType;
    queryParams: GetAgencyBranchesListParams | null;
  };
  agencyContactList: {
    loading: boolean;
    list: AgencyContactType[];
    pagination: PaginationType;
    queryParams: GetAgencyContactsListParams | null;
  };
  agencyAssignmentList: {
    loading: boolean;
    list: Assignment[];
    pagination: PaginationType;
    queryParams: GetAssignmentListParams | null;
  };
  agencyEmployeeList: {
    loading: boolean;
    list: EmployeeRecordType[];
    pagination: PaginationType;
    queryParams: GetEmployeeListParams | null;
  };
};

const initialState: AgencyState = {
  agencyList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyDocumentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyTimesheetList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyBranchList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyContactList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyAssignmentList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  agencyEmployeeList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const agencySlice = createSlice({
  name: 'agency',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Agency Records
    builder.addCase(agencyActs.getAgencyList.pending, (state) => {
      state.agencyList.loading = true;
    });
    builder.addCase(agencyActs.getAgencyList.fulfilled, (state, action) => {
      state.agencyList.loading = false;
      state.agencyList.list = action.payload.data;
      state.agencyList.pagination = action.payload.pagination;
      state.agencyList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(agencyActs.getAgencyList.rejected, (state) => {
      state.agencyList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyListError',
        }),
      });
    });

    // Get Agency Document List Records
    builder.addCase(agencyActs.getAgencyDocumentListById.pending, (state) => {
      state.agencyDocumentList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyDocumentListById.fulfilled,
      (state, action) => {
        state.agencyDocumentList.loading = false;
        state.agencyDocumentList.list = action.payload.data;
        state.agencyDocumentList.pagination = action.payload.pagination;
        state.agencyDocumentList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(agencyActs.getAgencyDocumentListById.rejected, (state) => {
      state.agencyDocumentList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyDocumentListError',
        }),
      });
    });

    // Get Agency Timesheet List Records
    builder.addCase(agencyActs.getAgencyTimesheetListById.pending, (state) => {
      state.agencyTimesheetList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyTimesheetListById.fulfilled,
      (state, action) => {
        state.agencyTimesheetList.loading = false;
        state.agencyTimesheetList.list = action.payload.data;
        state.agencyTimesheetList.pagination = action.payload.pagination;
        state.agencyTimesheetList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(agencyActs.getAgencyTimesheetListById.rejected, (state) => {
      state.agencyTimesheetList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyTimesheetListError',
        }),
      });
    });

    // Get Agency Branch List Records
    builder.addCase(agencyActs.getAgencyBranchesById.pending, (state) => {
      state.agencyBranchList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyBranchesById.fulfilled,
      (state, action) => {
        state.agencyBranchList.loading = false;
        state.agencyBranchList.list = action.payload.data;
        state.agencyBranchList.pagination = action.payload.pagination;
        state.agencyBranchList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(agencyActs.getAgencyBranchesById.rejected, (state) => {
      state.agencyBranchList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyBranchesListError',
        }),
      });
    });

    // Get Agency Contact List Records
    builder.addCase(agencyActs.getAgencyContactsById.pending, (state) => {
      state.agencyContactList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyContactsById.fulfilled,
      (state, action) => {
        state.agencyContactList.loading = false;
        state.agencyContactList.list = action.payload.data;
        state.agencyContactList.pagination = action.payload.pagination;
        state.agencyContactList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(agencyActs.getAgencyContactsById.rejected, (state) => {
      state.agencyContactList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyContactsListError',
        }),
      });
    });

    // Get Assignment Records
    builder.addCase(agencyActs.getAgencyAssignmentList.pending, (state) => {
      state.agencyAssignmentList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyAssignmentList.fulfilled,
      (state, action) => {
        state.agencyAssignmentList.loading = false;
        state.agencyAssignmentList.list = action.payload.data;
        state.agencyAssignmentList.pagination = action.payload.pagination;
        state.agencyAssignmentList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      agencyActs.getAgencyAssignmentList.rejected,
      (state, action) => {
        state.agencyAssignmentList.loading = false;
        showToast({
          type: 'error',
          message:
            (action.payload as any)?.response?.data?.message ||
            appIntl().formatMessage({
              id: 'agency.getAgencyAssignmentListError',
            }),
        });
      },
    );

    // Get Agency Employee List Records
    builder.addCase(agencyActs.getAgencyEmployeeList.pending, (state) => {
      state.agencyEmployeeList.loading = true;
    });
    builder.addCase(
      agencyActs.getAgencyEmployeeList.fulfilled,
      (state, action) => {
        state.agencyEmployeeList.loading = false;
        state.agencyEmployeeList.list = action.payload.data;
        state.agencyEmployeeList.pagination = action.payload.pagination;
      },
    );
    builder.addCase(agencyActs.getAgencyEmployeeList.rejected, (state) => {
      state.agencyEmployeeList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'agency.getAgencyEmployeeListError',
        }),
      });
    });
  },
});

export const agencyReducer = agencySlice.reducer;
export const agencyAction = {
  ...agencySlice.actions,
  ...agencyActs,
};
